// Backlog 072-A — spawn the detached completion watcher.
//
// `cloudgrid plug --detach` queues the deploy synchronously, prints a
// confirmation, and forks a tiny background process that polls the trace
// to terminal state and fires the OS notification + deploys.log entry.
//
// Implementation notes:
//
// - We re-invoke the same Node binary (`process.argv[0]`) on the same CLI
//   entry point (`process.argv[1]`) with the hidden `_watch <trace>` verb.
//   That keeps the auth-resolution path identical to the parent (same
//   ~/.cloudgrid/credentials.json, same active org).
// - `detached: true` makes the child its own process-group leader so it
//   survives the parent shell exiting.
// - `stdio: 'ignore'` cuts every fd link to the parent — no EPIPE on
//   terminal close, no stray output.
// - `unref()` lets the parent's Node event loop exit immediately.
//
// The whole thing is wrapped in a try/catch and returns `{ ok: false }` on
// any spawn error so the caller can fall back gracefully (per kickoff
// stop-condition #5: "fall back to fire-and-forget when spawn isn't
// available").

import { spawn as defaultSpawn } from 'node:child_process';

export interface SpawnDetachedWatcherInput {
  traceId: string;
}

export interface SpawnDetachedWatcherDeps {
  /**
   * Override the spawn function. Tests pass a stub that records the
   * invocation without forking a real process. Defaults to
   * `child_process.spawn`.
   */
  spawn?: typeof defaultSpawn;
  /**
   * Override the node binary. Defaults to `process.argv[0]`.
   */
  nodeBinary?: string;
  /**
   * Override the CLI entry script. Defaults to `process.argv[1]`.
   */
  cliEntry?: string;
}

export interface SpawnResult {
  ok: boolean;
  pid?: number;
}

/**
 * Spawn the hidden `_watch <trace_id>` subcommand as a detached child.
 * Returns `{ ok: true, pid }` on success, `{ ok: false }` on any failure.
 * Never throws.
 */
export function spawnDetachedWatcher(
  input: SpawnDetachedWatcherInput,
  deps: SpawnDetachedWatcherDeps = {},
): SpawnResult {
  const spawnFn = deps.spawn ?? defaultSpawn;
  const node = deps.nodeBinary ?? process.argv[0];
  const entry = deps.cliEntry ?? process.argv[1];

  if (!node || !entry) return { ok: false };

  try {
    const child = spawnFn(node, [entry, '_watch', input.traceId], {
      detached: true,
      stdio: 'ignore',
      // Inherit env so the watcher reads the same credentials + config.
    });
    if (typeof child.unref === 'function') child.unref();
    return { ok: true, pid: child.pid };
  } catch {
    return { ok: false };
  }
}
